import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Home, MapPin, Camera, Upload, Loader2, ImageIcon, ShieldOff } from "lucide-react";
import { uploadDirectToDrive } from "@/lib/uploadDirectToDrive";  // PR #105 / APP-6

const PROPERTY_TYPES = ["Residential", "Commercial", "Rural", "Industrial", "Vacant Land", "Other"];
const VALUATION_TYPES = ["Single Story", "Double Story", "Garage", "Carport", "Off-street"];

const emptyProperty = {
  address: "",
  property_type: "",
  property_type_other: "",
  first_mortgagee: "",
  second_mortgagee: "",
  bedrooms: "",
  bathrooms: "",
  car_spaces: "",
  first_mortgage_debt: 0,
  second_mortgage_debt: 0,
  building_size: "",
  land_size: "",
  valuation_type: [],
  est_current_value: 0,
  purchase_price: 0,
  is_owner_occupied: true,
};

export const StepSecurityDetails = ({ formData, updateFormData, applicationId, API }) => {
  const [properties, setProperties] = useState(
    formData.properties.length > 0 ? formData.properties : [{ ...emptyProperty }]
  );
  const [activeTab, setActiveTab] = useState("0");
  // PR #105 / APP-6 — property photos via the existing direct-to-Drive flow.
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photos, setPhotos] = useState(
    (formData.uploaded_documents || []).filter((d) => d.category === "Property Photos")
  );

  // APP-FORM-FIX (2026-07-25) — some loans aren't secured against any
  // property. Previously this step always rendered one property row with a
  // visually-required address and no way to opt out, so unsecured-loan
  // clients either invented a placeholder or got stuck. `no_security_property`
  // is persisted on the application (backend/models.py) so admin/PDF/lender
  // packs can treat "no security" as a real, explicit state rather than
  // inferring it from an empty array.
  const [noSecurityProperty, setNoSecurityProperty] = useState(!!formData.no_security_property);
  // Session-only stash of property rows so toggling the checkbox on/off
  // doesn't destroy anything the client already typed.
  const stashedPropertiesRef = useRef(null);

  const handleNoSecurityToggle = (checked) => {
    setNoSecurityProperty(checked);
    updateFormData("no_security_property", checked);
    if (checked) {
      stashedPropertiesRef.current = properties;
      setProperties([]);
      setActiveTab("0");
    } else {
      const restored = stashedPropertiesRef.current && stashedPropertiesRef.current.length > 0
        ? stashedPropertiesRef.current
        : [{ ...emptyProperty }];
      stashedPropertiesRef.current = null;
      setProperties(restored);
    }
  };

  useEffect(() => {
    updateFormData("properties", properties);
  }, [properties, updateFormData]);

  // Re-derive the photos list any time the parent's uploaded_documents
  // changes (e.g. on initial draft load).
  useEffect(() => {
    setPhotos((formData.uploaded_documents || []).filter((d) => d.category === "Property Photos"));
  }, [formData.uploaded_documents]);

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    if (!applicationId) {
      toast.error("Save the application first — we need a deal reference before attaching photos.");
      event.target.value = "";
      return;
    }
    // Image-only filter for sanity; lender packs really only need JPG/PNG/HEIC.
    const allowed = files.filter((f) => /^image\//.test(f.type));
    if (allowed.length === 0) {
      toast.error("Please pick image files (JPG / PNG / HEIC).");
      event.target.value = "";
      return;
    }
    setUploadingPhoto(true);
    try {
      for (const file of allowed) {
        setUploadProgress(0);
        await uploadDirectToDrive(
          file,
          { target_kind: "application_general", application_id: applicationId, category: "Property Photos" },
          { onProgress: (frac) => setUploadProgress(Math.round(frac * 100)) },
        );
      }
      // Re-fetch the application so the parent's uploaded_documents picks up
      // the newly-attached photos.
      try {
        const res = await axios.get(`${API}/applications/${applicationId}`);
        const docs = res.data?.uploaded_documents || [];
        updateFormData("uploaded_documents", docs);
        setPhotos(docs.filter((d) => d.category === "Property Photos"));
      } catch (_) {
        // Non-fatal — they'll show on the next save / step navigation.
      }
      toast.success(`${allowed.length} photo${allowed.length === 1 ? "" : "s"} uploaded`);
    } catch (err) {
      toast.error(err?.message || "Photo upload failed");
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(0);
      event.target.value = "";
    }
  };

  const removePhoto = async (photo) => {
    if (!applicationId) return;
    if (!photo?.id) return;
    try {
      await axios.delete(`${API}/applications/${applicationId}/documents/${photo.id}`);
      const next = photos.filter((p) => p.id !== photo.id);
      setPhotos(next);
      // Mirror into formData.uploaded_documents so other steps see the same.
      const otherDocs = (formData.uploaded_documents || []).filter((d) => d.id !== photo.id);
      updateFormData("uploaded_documents", otherDocs);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove photo");
    }
  };

  const handlePropertyChange = (index, field, value) => {
    const newProperties = [...properties];
    newProperties[index] = { ...newProperties[index], [field]: value };
    setProperties(newProperties);
  };

  const handleValuationTypeChange = (index, type, checked) => {
    const property = properties[index];
    let newTypes = [...(property.valuation_type || [])];
    
    if (checked) {
      if (!newTypes.includes(type)) {
        newTypes.push(type);
      }
    } else {
      newTypes = newTypes.filter((t) => t !== type);
    }
    
    handlePropertyChange(index, "valuation_type", newTypes);
  };

  const addProperty = () => {
    if (properties.length < 5) {
      const newProperties = [...properties, { ...emptyProperty }];
      setProperties(newProperties);
      setActiveTab(String(newProperties.length - 1));
    }
  };

  const removeProperty = (index) => {
    if (properties.length > 1) {
      const newProperties = properties.filter((_, i) => i !== index);
      setProperties(newProperties);
      setActiveTab("0");
    }
  };

  const renderPropertyForm = (property, index) => (
    <div className="space-y-6" data-testid={`property-form-${index}`}>
      {/* Property Address */}
      <section className="form-card rounded">
        <h4 className="section-subheader flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#bd9a59]" />
          Property Address
        </h4>
        
        <div>
          {/* APP-FORM-FIX (2026-07-25) — address is no longer visually
              "required" (form-label-required added a red asterisk even
              though nothing ever actually validated it). Some clients
              are still confirming the property, or offering security
              that doesn't have a settled address yet — a soft prompt
              nudges without blocking. */}
          <Label className="form-label">Full Address</Label>
          <Input
            value={property.address}
            onChange={(e) => handlePropertyChange(index, "address", e.target.value)}
            placeholder="e.g. 12 Smith Street, Sydney NSW 2000"
            className="form-input"
            data-testid={`property-address-${index}`}
          />
          {!property.address?.trim() && (
            <p className="text-xs text-amber-600 mt-1.5" data-testid={`property-address-soft-prompt-${index}`}>
              Optional — but adding the address helps your broker move faster on this application.
            </p>
          )}
        </div>
      </section>

      {/* Property Type */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Property Type</h4>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {PROPERTY_TYPES.map((type) => (
            <div key={type} className="flex items-center gap-2">
              <Checkbox
                id={`prop-type-${index}-${type}`}
                checked={property.property_type === type}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handlePropertyChange(index, "property_type", type);
                  }
                }}
                data-testid={`property-type-${type.toLowerCase()}-${index}`}
              />
              <Label htmlFor={`prop-type-${index}-${type}`} className="cursor-pointer text-sm">
                {type}
              </Label>
            </div>
          ))}
        </div>
        
        {property.property_type === "Other" && (
          <div className="mt-4">
            <Label className="form-label">Please specify</Label>
            <Input
              value={property.property_type_other}
              onChange={(e) => handlePropertyChange(index, "property_type_other", e.target.value)}
              placeholder="Specify property type"
              className="form-input max-w-md"
              data-testid={`property-type-other-${index}`}
            />
          </div>
        )}
      </section>

      {/* Current Mortgagee Details */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Current Mortgagee Details</h4>
        
        <div className="form-grid form-grid-2">
          <div>
            <Label className="form-label">1st Mortgagee Name</Label>
            <Input
              value={property.first_mortgagee}
              onChange={(e) => handlePropertyChange(index, "first_mortgagee", e.target.value)}
              placeholder="Current lender name"
              className="form-input"
              data-testid={`property-first-mortgagee-${index}`}
            />
          </div>
          <div>
            <Label className="form-label">2nd Mortgagee Name</Label>
            <Input
              value={property.second_mortgagee}
              onChange={(e) => handlePropertyChange(index, "second_mortgagee", e.target.value)}
              placeholder="If applicable"
              className="form-input"
              data-testid={`property-second-mortgagee-${index}`}
            />
          </div>
          <div>
            <Label className="form-label">1st Mortgage Debt Position ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                value={property.first_mortgage_debt || ""}
                onChange={(e) => handlePropertyChange(index, "first_mortgage_debt", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="form-input pl-8 text-right"
                data-testid={`property-first-mortgage-debt-${index}`}
              />
            </div>
          </div>
          <div>
            <Label className="form-label">2nd Mortgage Debt Position ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                value={property.second_mortgage_debt || ""}
                onChange={(e) => handlePropertyChange(index, "second_mortgage_debt", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="form-input pl-8 text-right"
                data-testid={`property-second-mortgage-debt-${index}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Property Description */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Property Description</h4>
        
        <div className="form-grid form-grid-3">
          <div>
            <Label className="form-label">Bedrooms</Label>
            <Input
              value={property.bedrooms}
              onChange={(e) => handlePropertyChange(index, "bedrooms", e.target.value)}
              placeholder="Number of bedrooms"
              className="form-input"
              data-testid={`property-bedrooms-${index}`}
            />
          </div>
          <div>
            <Label className="form-label">Bathrooms</Label>
            <Input
              value={property.bathrooms}
              onChange={(e) => handlePropertyChange(index, "bathrooms", e.target.value)}
              placeholder="Number of bathrooms"
              className="form-input"
              data-testid={`property-bathrooms-${index}`}
            />
          </div>
          <div>
            <Label className="form-label">Car Spaces</Label>
            <Input
              value={property.car_spaces}
              onChange={(e) => handlePropertyChange(index, "car_spaces", e.target.value)}
              placeholder="Number of spaces"
              className="form-input"
              data-testid={`property-car-spaces-${index}`}
            />
          </div>
          <div>
            <Label className="form-label">Building Size (sqm)</Label>
            <Input
              value={property.building_size}
              onChange={(e) => handlePropertyChange(index, "building_size", e.target.value)}
              placeholder="e.g., 200"
              className="form-input"
              data-testid={`property-building-size-${index}`}
            />
          </div>
          <div>
            <Label className="form-label">Land Size (sqm)</Label>
            <Input
              value={property.land_size}
              onChange={(e) => handlePropertyChange(index, "land_size", e.target.value)}
              placeholder="e.g., 650"
              className="form-input"
              data-testid={`property-land-size-${index}`}
            />
          </div>
        </div>

        <div className="mt-6">
          <Label className="form-label mb-3">Property Features</Label>
          <div className="flex flex-wrap gap-4">
            {VALUATION_TYPES.map((type) => (
              <div key={type} className="flex items-center gap-2">
                <Checkbox
                  id={`val-type-${index}-${type}`}
                  checked={(property.valuation_type || []).includes(type)}
                  onCheckedChange={(checked) => handleValuationTypeChange(index, type, checked)}
                  data-testid={`property-valuation-${type.toLowerCase().replace(' ', '-')}-${index}`}
                />
                <Label htmlFor={`val-type-${index}-${type}`} className="cursor-pointer text-sm">
                  {type}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valuation Details */}
      <section className="form-card rounded">
        <h4 className="section-subheader">Valuation Details</h4>
        
        <div className="form-grid form-grid-2">
          <div>
            <Label className="form-label">Estimated Current Value ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                value={property.est_current_value || ""}
                onChange={(e) => handlePropertyChange(index, "est_current_value", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="form-input pl-8 text-right"
                data-testid={`property-est-value-${index}`}
              />
            </div>
          </div>
          <div>
            <Label className="form-label">Purchase Price ($)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                value={property.purchase_price || ""}
                onChange={(e) => handlePropertyChange(index, "purchase_price", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="form-input pl-8 text-right"
                data-testid={`property-purchase-price-${index}`}
              />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Label className="form-label mb-3">Is the property Owner Occupied or Investment?</Label>
          <RadioGroup
            value={property.is_owner_occupied ? "owner" : "investment"}
            onValueChange={(v) => handlePropertyChange(index, "is_owner_occupied", v === "owner")}
            className="flex gap-6"
            data-testid={`property-occupancy-${index}`}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="owner" id={`occ-owner-${index}`} />
              <Label htmlFor={`occ-owner-${index}`} className="cursor-pointer">Owner Occupied</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="investment" id={`occ-invest-${index}`} />
              <Label htmlFor={`occ-invest-${index}`} className="cursor-pointer">Investment</Label>
            </div>
          </RadioGroup>
        </div>
      </section>

      {/* Remove Property Button */}
      {properties.length > 1 && (
        <div className="flex justify-end">
          <button
            onClick={() => removeProperty(index)}
            className="text-red-500 hover:text-red-700 flex items-center gap-2 px-4 py-2 rounded border border-red-200 hover:bg-red-50 transition-colors"
            data-testid={`remove-property-${index}`}
          >
            <Trash2 className="w-4 h-4" />
            Remove Property {index + 1}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* APP-FORM-FIX (2026-07-25) — explicit opt-out for unsecured loans.
          Placed above everything else on this step so it's the first thing
          a borrower with no security property sees. */}
      <div className="form-card rounded p-6" data-testid="no-security-property-toggle">
        <div className="flex items-start gap-3">
          <Checkbox
            id="no-security-property"
            checked={noSecurityProperty}
            onCheckedChange={handleNoSecurityToggle}
            data-testid="no-security-property-checkbox"
          />
          <div>
            <Label htmlFor="no-security-property" className="cursor-pointer font-medium text-[#021d49]">
              This loan has no security property
            </Label>
            <p className="text-sm text-gray-500 mt-1">
              Tick this if you're not offering any property as security for this loan. You can leave the
              rest of this step blank — untick any time before submitting if that changes.
            </p>
          </div>
        </div>
      </div>

      {noSecurityProperty ? (
        <div
          className="form-card rounded p-6 flex items-start gap-3 text-sm text-gray-500"
          data-testid="no-security-property-notice"
        >
          <ShieldOff className="w-5 h-5 text-[#bd9a59] flex-shrink-0 mt-0.5" />
          <p>No security property will be included with this application. Untick the box above if you'd like to add one.</p>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              {/* PR #105 / APP-8 — copy review: anchors the user in WHY they're entering security
                  details, not just WHAT to enter. */}
              <p className="text-sm text-gray-500">
                Add the property (or properties — up to 5) you'll offer as security for this loan.
                Photos are optional but lenders move faster when they have them.
              </p>
            </div>
            {properties.length < 5 && (
              <button
                onClick={addProperty}
                className="btn-secondary px-4 py-2 rounded flex items-center gap-2"
                data-testid="add-property-btn"
              >
                <Plus className="w-4 h-4" />
                Add Property
              </button>
            )}
          </div>

          {properties.length === 1 ? (
            renderPropertyForm(properties[0], 0)
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start mb-6 bg-gray-100 p-1 rounded overflow-x-auto">
                {properties.map((p, index) => (
                  <TabsTrigger
                    key={index}
                    value={String(index)}
                    className="flex items-center gap-2 data-[state=active]:bg-[#021d49] data-[state=active]:text-white rounded px-4 py-2 flex-shrink-0"
                    data-testid={`property-tab-${index}`}
                  >
                    <Home className="w-4 h-4" />
                    Property {index + 1}
                  </TabsTrigger>
                ))}
              </TabsList>

              {properties.map((property, index) => (
                <TabsContent key={index} value={String(index)}>
                  {renderPropertyForm(property, index)}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </>
      )}

      {/* PR #105 / APP-6 — Property photos. Application-level for v1; per-property assignment
          is a follow-up if Mark wants it. Photos land in the application's Drive folder under
          "Property Photos" so lender submission packs and ID-pack PDFs can pull them.
          APP-FORM-FIX (2026-07-25) — hidden when no security property applies, nothing to photograph. */}
      {!noSecurityProperty && (
      <section className="form-card rounded" data-testid="property-photos-section">
        <h4 className="section-subheader flex items-center gap-2">
          <Camera className="w-5 h-5 text-[#bd9a59]" />
          Property Photos
        </h4>
        <p className="text-sm text-gray-500 mb-3">
          Snap a couple of street-view + interior shots. Lenders often want these for the submission pack.
          {!applicationId && <span className="block text-amber-700 mt-1">Save the application first — we need a deal reference before attaching photos.</span>}
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label
            className={`inline-flex items-center gap-2 px-4 py-2 rounded text-sm cursor-pointer transition-colors ${
              uploadingPhoto || !applicationId
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-[#bd9a59] text-white hover:bg-[#9c7e46]"
            }`}
            data-testid="property-photo-upload-trigger"
          >
            {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploadingPhoto ? `Uploading… ${uploadProgress}%` : "Upload photos"}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              disabled={uploadingPhoto || !applicationId}
              onChange={handlePhotoUpload}
              className="hidden"
              data-testid="property-photo-input"
            />
          </label>
          <span className="text-xs text-gray-500">JPG / PNG / HEIC · {photos.length} photo{photos.length === 1 ? "" : "s"} attached</span>
        </div>

        {photos.length === 0 ? (
          <div className="bg-gray-50 border border-dashed border-gray-200 rounded p-6 text-center text-sm text-gray-400" data-testid="property-photos-empty">
            <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            No photos uploaded yet
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="property-photos-gallery">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group bg-white border border-gray-200 rounded overflow-hidden" data-testid={`property-photo-${photo.id}`}>
                {/* Thumbnail — if Drive returned a thumbnail link use it, otherwise fall back to the
                    web view link with a download-style preview. */}
                {photo.thumbnail_link || photo.drive_web_content_link ? (
                  <img
                    src={photo.thumbnail_link || photo.drive_web_content_link}
                    alt={photo.file_name}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-32 flex items-center justify-center bg-gray-100">
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  </div>
                )}
                <div className="p-2 text-xs">
                  <p className="font-medium text-[#021d49] truncate" title={photo.file_name}>{photo.file_name}</p>
                </div>
                <button
                  onClick={() => removePhoto(photo)}
                  className="absolute top-1 right-1 p-1.5 bg-white/90 hover:bg-red-50 rounded text-red-500 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                  aria-label={`Remove ${photo.file_name}`}
                  data-testid={`property-photo-remove-${photo.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      )}
    </div>
  );
};
